import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Loader2, ShieldCheck, LogIn } from "lucide-react";
import PageHero from "@/components/marketing/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import SecretWarning from "@/components/SecretWarning";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { getClientTypes, getProblemTypes, getMaterialOptions, getWallets } from "@/data/site";

const initial = {
  client_type: "individual", country: "DE", preferred_language: "de",
  wallet_type: "", wallet_software: "", creation_year: "", cryptocurrency: "",
  operating_system: "", public_address: "", approx_value: "",
  problem_type: "", available_material: [],
  description: "", confirm_ownership: false, consent_review: false,
  confirm_no_secrets: false, accept_privacy: false,
};

export default function AvviaRecupero() {
  const { user, loading } = useAuth();
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const STEPS = [
    L("Dettagli", "Details", "Details"),
    L("Wallet", "Wallet", "Wallet"),
    L("Problema", "Problem", "Problem"),
    L("Materiale", "Material", "Unterlagen"),
    L("Dichiarazioni", "Declarations", "Erklärungen"),
  ];
  const clientTypes = getClientTypes(lang);
  const problemTypes = getProblemTypes(lang);
  const materialOptions = getMaterialOptions(lang);
  const wallets = getWallets(lang);

  const toggleMaterial = (v) => {
    setForm((f) => ({
      ...f,
      available_material: f.available_material.includes(v)
        ? f.available_material.filter((x) => x !== v)
        : [...f.available_material, v],
    }));
  };

  const canNext = () => {
    if (step === 1) return !!form.wallet_type;
    if (step === 2) return !!form.problem_type;
    if (step === 4) return form.description.length > 10 && form.confirm_ownership && form.consent_review && form.confirm_no_secrets && form.accept_privacy;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/cases", form);
      toast.success(L(`Pratica ${data.reference} creata`, `Case ${data.reference} created`, `Fall ${data.reference} erstellt`));
      navigate(`/portal/cases/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyanx" /></div>;

  if (!user) {
    return (
      <div>
        <PageHero eyebrow={L("Avvia un recupero", "Start a recovery", "Wiederherstellung starten")} title={L("Richiedi una valutazione riservata", "Request a confidential assessment", "Vertrauliche Bewertung anfragen")}
          subtitle={L("Per aprire una pratica accedi o crea un account. In alternativa contatta il team.", "To open a case, sign in or create an account. Alternatively, contact the team.", "Um einen Fall zu eröffnen, melden Sie sich an oder erstellen Sie ein Konto. Alternativ kontaktieren Sie das Team.")} />
        <section className="mx-auto max-w-md px-4 py-20">
          <div className="rounded-xl border border-white/10 bg-navy-paper p-8 text-center">
            <ShieldCheck className="mx-auto h-10 w-10 text-cyanx" />
            <p className="mt-4 text-slate-300">{L("Accedi o registrati per compilare il modulo in modo sicuro e seguire la tua pratica.", "Sign in or register to fill out the form securely and track your case.", "Melden Sie sich an oder registrieren Sie sich, um das Formular sicher auszufüllen und Ihren Fall zu verfolgen.")}</p>
            <div className="mt-6 flex flex-col gap-3">
              <Button asChild className="bg-gold text-navy hover:bg-gold-hover"><Link to="/registrazione"><LogIn className="h-4 w-4 mr-2" />{L("Registrati", "Register", "Registrieren")}</Link></Button>
              <Button asChild variant="outline" className="border-white/20 text-white"><Link to="/login">{L("Ho già un account", "I already have an account", "Ich habe bereits ein Konto")}</Link></Button>
              <Link to="/contatti" className="text-sm text-cyanx hover:text-white mt-2">{L("Preferisci contattarci?", "Prefer to contact us?", "Möchten Sie uns lieber kontaktieren?")} →</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (user.role !== "client") {
    return (
      <div className="mx-auto max-w-md px-4 py-32 text-center">
        <p className="text-slate-300">{L("L'apertura pratiche è riservata agli account cliente.", "Opening cases is reserved for client accounts.", "Das Eröffnen von Fällen ist Kundenkonten vorbehalten.")}</p>
        <Button asChild className="mt-6 bg-gold text-navy"><Link to="/">{L("Torna alla home", "Back to home", "Zurück zur Startseite")}</Link></Button>
      </div>
    );
  }

  const inputCls = "mt-1.5 bg-navy border-white/10";

  return (
    <div className="bg-navy min-h-screen">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <h1 className="font-heading text-3xl font-medium text-white">{L("Avvia un recupero", "Start a recovery", "Wiederherstellung starten")}</h1>
        <p className="mt-2 text-slate-400">{L("Compila i passaggi seguenti. Non inserire segreti del wallet.", "Complete the following steps. Do not enter wallet secrets.", "Füllen Sie die folgenden Schritte aus. Geben Sie keine Wallet-Geheimnisse ein.")}</p>

        {/* Stepper */}
        <div className="mt-8 flex items-center gap-2" data-testid="wizard-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${i <= step ? "text-cyanx" : "text-slate-600"}`}>
                <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs border ${i < step ? "bg-cyanx text-navy border-cyanx" : i === step ? "border-cyanx" : "border-slate-700"}`}>
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className="hidden sm:inline text-xs">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-cyanx" : "bg-slate-800"}`} />}
            </React.Fragment>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
          className="mt-8 rounded-xl border border-white/10 bg-navy-paper p-6 sm:p-8" data-testid={`wizard-step-${step}`}>

          {step === 0 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl text-white">{L("Dettagli", "Details", "Details")}</h2>
              <div>
                <Label className="text-slate-300">{L("Tipo di richiedente", "Applicant type", "Art des Antragstellers")}</Label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {clientTypes.map((c) => (
                    <button key={c.value} type="button" onClick={() => set("client_type", c.value)} data-testid={`client-type-${c.value}`}
                      className={`rounded-lg border px-3 py-2 text-sm ${form.client_type === c.value ? "border-cyanx bg-cyanx/10 text-cyanx" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300">{L("Paese", "Country", "Land")}</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} data-testid="wizard-country" /></div>
                <div><Label className="text-slate-300">{L("Lingua preferita", "Preferred language", "Bevorzugte Sprache")}</Label>
                  <select value={form.preferred_language} onChange={(e) => set("preferred_language", e.target.value)} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="wizard-language">
                    <option value="de">Deutsch</option><option value="it">Italiano</option><option value="en">English</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl text-white">{L("Dettagli del wallet", "Wallet details", "Wallet-Details")}</h2>
              <div>
                <Label className="text-slate-300">{L("Tipo di wallet", "Wallet type", "Wallet-Typ")} *</Label>
                <select value={form.wallet_type} onChange={(e) => set("wallet_type", e.target.value)} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="wizard-wallet-type">
                  <option value="">{L("Seleziona…", "Select…", "Auswählen…")}</option>
                  {wallets.map((w) => <option key={w.slug} value={w.slug}>{w.name}</option>)}
                  <option value="other">{L("Altro", "Other", "Sonstiges")}</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300">{L("Software / dispositivo", "Software / device", "Software / Gerät")}</Label><Input value={form.wallet_software} onChange={(e) => set("wallet_software", e.target.value)} className={inputCls} data-testid="wizard-software" /></div>
                <div><Label className="text-slate-300">{L("Anno di creazione (circa)", "Creation year (approx.)", "Erstellungsjahr (ca.)")}</Label><Input value={form.creation_year} onChange={(e) => set("creation_year", e.target.value)} className={inputCls} data-testid="wizard-year" /></div>
                <div><Label className="text-slate-300">{L("Criptovaluta / rete", "Cryptocurrency / network", "Kryptowährung / Netzwerk")}</Label><Input value={form.cryptocurrency} onChange={(e) => set("cryptocurrency", e.target.value)} className={inputCls} data-testid="wizard-crypto" /></div>
                <div><Label className="text-slate-300">{L("Sistema operativo / dispositivo", "Operating system / device", "Betriebssystem / Gerät")}</Label><Input value={form.operating_system} onChange={(e) => set("operating_system", e.target.value)} className={inputCls} data-testid="wizard-os" /></div>
              </div>
              <div><Label className="text-slate-300">{L("Indirizzo pubblico (facoltativo)", "Public address (optional)", "Öffentliche Adresse (optional)")}</Label><Input value={form.public_address} onChange={(e) => set("public_address", e.target.value)} className={`${inputCls} font-mono text-xs`} data-testid="wizard-address" /></div>
              <div><Label className="text-slate-300">{L("Valore approssimativo (facoltativo, autodichiarato)", "Approximate value (optional, self-declared)", "Ungefährer Wert (optional, Selbstauskunft)")}</Label><Input value={form.approx_value} onChange={(e) => set("approx_value", e.target.value)} className={inputCls} data-testid="wizard-value" /></div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl text-white">{L("Tipo di problema", "Problem type", "Art des Problems")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {problemTypes.map((p) => (
                  <button key={p.value} type="button" onClick={() => set("problem_type", p.value)} data-testid={`problem-${p.value}`}
                    className={`text-left rounded-lg border px-4 py-3 text-sm ${form.problem_type === p.value ? "border-cyanx bg-cyanx/10 text-cyanx" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl text-white">{L("Materiale disponibile", "Available material", "Verfügbare Unterlagen")}</h2>
              <p className="text-sm text-slate-400">{L("Indica cosa possiedi ancora. Questo aiuta a valutare la fattibilità.", "Indicate what you still have. This helps assess feasibility.", "Geben Sie an, was Sie noch besitzen. Das hilft bei der Machbarkeitsbewertung.")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {materialOptions.map((m) => (
                  <label key={m.value} className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm cursor-pointer ${form.available_material.includes(m.value) ? "border-cyanx bg-cyanx/10 text-cyanx" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
                    <Checkbox checked={form.available_material.includes(m.value)} onCheckedChange={() => toggleMaterial(m.value)} data-testid={`material-${m.value}`} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl text-white">{L("Descrizione e dichiarazioni", "Description and declarations", "Beschreibung und Erklärungen")}</h2>
              <SecretWarning />
              <div>
                <Label className="text-slate-300">{L("Descrizione dettagliata del caso", "Detailed case description", "Detaillierte Fallbeschreibung")} *</Label>
                <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="wizard-description"
                  placeholder={L("Descrivi cosa è successo, senza inserire seed phrase complete, chiavi private, PIN o password complete.", "Describe what happened, without entering full seed phrases, private keys, PINs or full passwords.", "Beschreiben Sie, was passiert ist, ohne vollständige Seed-Phrasen, private Schlüssel, PINs oder vollständige Passwörter einzugeben.")} />
              </div>
              <div className="space-y-2.5">
                {[
                  ["confirm_ownership", L("Confermo di essere il legittimo titolare o di avere autorità legale sul wallet", "I confirm I am the rightful owner or have legal authority over the wallet", "Ich bestätige, dass ich der rechtmäßige Inhaber bin oder rechtliche Befugnis über das Wallet habe")],
                  ["consent_review", L("Acconsento alla revisione preliminare del caso", "I consent to the preliminary review of the case", "Ich stimme der vorläufigen Prüfung des Falls zu")],
                  ["confirm_no_secrets", L("Confermo di non aver inserito seed phrase complete o chiavi private", "I confirm I have not entered full seed phrases or private keys", "Ich bestätige, dass ich keine vollständigen Seed-Phrasen oder privaten Schlüssel eingegeben habe")],
                  ["accept_privacy", L("Accetto la Privacy Policy", "I accept the Privacy Policy", "Ich akzeptiere die Datenschutzerklärung")],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer">
                    <Checkbox checked={form[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`decl-${k}`} className="mt-0.5" />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button type="button" variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="text-slate-300" data-testid="wizard-back">
              <ChevronLeft className="h-4 w-4 mr-1" />{L("Indietro", "Back", "Zurück")}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button type="button" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="bg-cyanx text-navy hover:bg-cyanx-hover" data-testid="wizard-next">
                {L("Avanti", "Next", "Weiter")} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button type="button" disabled={!canNext() || submitting} onClick={submit} className="bg-gold text-navy hover:bg-gold-hover" data-testid="wizard-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : L("Invia pratica", "Submit case", "Fall einreichen")}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
